// Refunds and chargebacks (sculpt-api PLAN-payments.md §7, M5): giving money back on a paid order — to the card
// through Mollie, or recorded for an order paid by transfer — with a credit note of the amount, and the chargebacks
// a cardholder's bank made.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { errorMessage, useAsync } from '../hooks/useAsync';
import { dateTime, money } from '../services/format';
import { Alert, Badge, Button, Card, Cell, Confirm, Field, Input, Row, Table, type Tone } from './ui';
import type { Order, RefundAdmin, RefundEffect } from '../types/api';

const SUBSCRIPTION_KINDS = ['subscribe', 'change', 'seats', 'renewal'];
const STATUS_TONE: Record<string, Tone> = { refunded: 'good', failed: 'bad', canceled: 'neutral' };

/** The effects a refund of this order may have: ending the subscription (a subscription order) or taking back the
 *  pack's unused credits (a pack). */
export function refundEffects(kind: string): RefundEffect[] {
  if (SUBSCRIPTION_KINDS.includes(kind)) return ['none', 'cancel_at_period_end', 'cancel_now'];
  if (kind === 'pack') return ['none', 'remove_credits'];
  return ['none'];
}

/** What is left to refund on the order, incl. VAT, as a two-decimal string. */
export function refundLeft(order: Pick<Order, 'total' | 'amount' | 'refunded'>): string {
  const total = Number(order.total ?? order.amount);
  return Math.max(0, total - Number(order.refunded ?? 0)).toFixed(2);
}

/** « Contestation » on an order or a payment the cardholder's bank took back. */
export function ChargebackBadge({ amount, at, currency = 'EUR' }: { amount?: string | null; at?: string | null; currency?: string }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const detail = amount ? ` · ${money(amount, currency, l)}${at ? ` · ${dateTime(at, l)}` : ''}` : '';
  return (
    <span data-testid="chargeback">
      <Badge tone="bad">{t('refunds.chargeback')}{detail}</Badge>
    </span>
  );
}

/** « Remboursements » / « Masquer » for a paid order, and « Remboursé » when something was. */
export function RefundsToggle({ order, open, onToggle }: { order: Pick<Order, 'id' | 'status' | 'refunded'>; open: boolean; onToggle: (id: number | null) => void }) {
  const { t } = useTranslation();
  if (order.status !== 'paid') return null;
  return (
    <Button onClick={() => onToggle(open ? null : order.id)} data-testid={`refunds-${order.id}`}>
      {open ? t('refunds.hide') : t('refunds.show')}
    </Button>
  );
}

/** A paid order's refunds and the form to make one. `onRefunded` reloads the page's orders. */
export function OrderRefunds({ order, onClose, onRefunded }: {
  order: Pick<Order, 'id' | 'kind' | 'status' | 'currency' | 'total' | 'amount' | 'refunded' | 'provider'>;
  onClose: () => void;
  onRefunded?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const { data, error, loading, reload } = useAsync(() => api.orderRefunds(order.id), [order.id]);
  const left = refundLeft(order);
  const [amount, setAmount] = useState(left);
  const [reason, setReason] = useState('');
  const [effect, setEffect] = useState<RefundEffect>('none');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const [done, setDone] = useState<RefundAdmin | null>(null);
  const byCard = order.provider === 'mollie';
  const valid = Number(amount) > 0 && Number(amount) <= Number(left) && reason.trim().length > 0;

  const refund = async () => {
    setBusy(true);
    setFail(null);
    try {
      const made = await api.refundOrder(order.id, { amount: Number(amount).toFixed(2), reason: reason.trim(), effect });
      setDone(made);
      setConfirming(false);
      setReason('');
      reload();
      onRefunded?.();
    } catch (err) {
      setFail(errorMessage(err));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      <Confirm
        open={confirming}
        title={t('refunds.confirmTitle', { id: order.id })}
        message={t(byCard ? 'refunds.confirmCard' : 'refunds.confirmTransfer', { amount: money(Number(amount).toFixed(2), order.currency, l) })}
        confirmLabel={t('refunds.refund')}
        danger
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void refund()}
      />
      <Card title={t('refunds.title', { id: order.id })} actions={<Button onClick={onClose}>{t('refunds.hide')}</Button>}>
        {(error || fail) && <div className="mb-3"><Alert>{fail || error}</Alert></div>}
        {done && (
          <div className="mb-3" data-testid="refund-done">
            <Alert tone="good">
              {t('refunds.done', { amount: money(done.amount, done.currency, l) })}
              {Number(done.amount).toFixed(2) !== Number(amount).toFixed(2) && ` ${t('refunds.rounded')}`}
            </Alert>
          </div>
        )}
        <Table head={[t('refunds.when'), t('refunds.amount'), t('refunds.how'), t('refunds.status'), t('refunds.reason'), t('refunds.effect'), t('refunds.creditNote')]}
               empty={loading ? t('common.loading') : t('refunds.none')}>
          {(data ?? []).map((r) => (
            <Row key={r.id}>
              <Cell mono>{dateTime(r.created_at, l)}</Cell>
              <Cell mono>{money(r.amount, r.currency, l)}</Cell>
              <Cell>{r.provider === 'mollie' ? t('refunds.toCard') : t('refunds.byTransfer')}</Cell>
              <Cell><Badge tone={STATUS_TONE[r.status] ?? 'warn'}>{t(`refunds.statuses.${r.status}`, { defaultValue: r.status })}</Badge></Cell>
              <Cell className="max-w-[240px] truncate" title={r.reason}>{r.reason || '—'}</Cell>
              <Cell>{t(`refunds.effects.${r.effect}`)}</Cell>
              <Cell>
                {r.credit_note_id ? (
                  <Link to={`/invoices/${r.credit_note_id}`} className="text-accent underline">{t('refunds.openCreditNote')}</Link>
                ) : '—'}
              </Cell>
            </Row>
          ))}
        </Table>
        {Number(left) > 0 ? (
          <form className="mt-4 flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (valid) setConfirming(true); }} data-testid="refund-form">
            <p className="text-[13px] text-muted">{t(byCard ? 'refunds.hintCard' : 'refunds.hintTransfer', { left: money(left, order.currency, l) })}</p>
            <div className="flex flex-wrap gap-3">
              <Field label={t('refunds.amountLabel')} hint={t('refunds.amountHint', { left: money(left, order.currency, l) })}>
                <Input type="number" step="0.01" min="0.01" max={left} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" />
              </Field>
              <Field label={t('refunds.reasonLabel')} hint={t('refunds.reasonHint')} className="min-w-[280px] flex-1">
                <Input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} data-testid="refund-reason" />
              </Field>
            </div>
            <fieldset className="flex flex-wrap gap-4 text-[13px]">
              <legend className="mb-1 text-[12px] text-muted">{t('refunds.effectLabel')}</legend>
              {refundEffects(order.kind).map((e) => (
                <label key={e} className="flex items-center gap-1.5">
                  <input type="radio" name={`effect-${order.id}`} checked={effect === e} onChange={() => setEffect(e)} data-testid={`effect-${e}`} />
                  {t(`refunds.effects.${e}`)}
                </label>
              ))}
            </fieldset>
            <div>
              <Button variant="danger" type="submit" disabled={!valid} data-testid="refund-submit">{t('refunds.refund')}</Button>
            </div>
          </form>
        ) : (
          <p className="mt-3 text-[13px] text-faint" data-testid="refund-nothing-left">{t('refunds.nothingLeft')}</p>
        )}
      </Card>
    </div>
  );
}
